import { computed, inject } from '@angular/core';
import {
  callApiMethod,
  withCommonOptions,
} from '@kinet-open-network/client/shared/data-access/state';
import { StockMovesService } from '@kinet-open-network/client/stock-moves/data-access';
import {
  IInvoiceStock,
  Option,
  VatRateType,
} from '@kinet-open-network/shared/data-access/model';
import {
  patchState,
  signalStore,
  withComputed,
  withMethods,
  withState,
} from '@ngrx/signals';
import { InvoiceOptions } from '../models/invoice-options.model';
import { InvoiceConnectorService } from '../services/invoice-connector.service';
import { InvoiceCustomersService } from '../services/invoice-customers.service';
import { InvoiceOptionsService } from '../services/invoice-options.service';
import { InvoicesService } from '../services/invoices.service';

interface InvoiceOptionsState {
  health: any | null;
  accountingOptions: (Option & { debit: string; credit: string })[];
  stockOptions: Option[];
}

export const InvoicesOptionsStore = signalStore(
  { protectedState: false },
  withCommonOptions<InvoiceOptions>(
    {
      billing: undefined,
      connection: undefined,
      items: { items: [] },
      vat: {
        defaultRate: VatRateType.standard,
        rounding: 2,
        vatRates: [
          { rate: VatRateType.standard, value: 23 },
          { rate: VatRateType.reduced1, value: 19 },
          { rate: VatRateType.reduced2, value: 5 },
          { rate: VatRateType.zero, value: 0 },
          { rate: VatRateType.none, value: 0 },
        ],
      },
      accounting: {
        payments: {},
      },
      rounding: { rules: [] },
      stocks: { stocks: [], groups: [], users: [] },
      documentTemplates: {},
      transactions: { accounts: [] },
    },
    InvoiceOptionsService,
  ),
  withState<InvoiceOptionsState>({
    health: null,
    accountingOptions: [],
    stockOptions: [],
  }),
  withMethods(
    (
      store,
      connector = inject(InvoiceConnectorService),
      invoices = inject(InvoicesService),
      customers = inject(InvoiceCustomersService),
      stockMoves = inject(StockMovesService),
    ) => ({
      loadClientHealth: () => {
        return callApiMethod(() => connector.health(), {
          next: (response) => {
            patchState(store, { health: response });
          },
        });
      },
      loadAccountingOptions: () => {
        return callApiMethod(() => connector.getAccountingOptions(), {
          next: (response) => {
            patchState(store, { accountingOptions: response });
          },
        });
      },
      loadStockOptions: () => {
        return callApiMethod(() => connector.getStocks(), {
          next: (stocks) => {
            patchState(store, {
              stockOptions: stocks.map<Option>((stock) => ({
                value: stock.id,
                label: stock.name,
              })),
            });
          },
        });
      },
      refreshItems: () => {
        return callApiMethod(() => invoices.refreshItems(), {});
      },
      refreshCustomers: () => {
        return callApiMethod(() => customers.refreshCustomers(), {});
      },
      importStockMoves: (from?: Date | null) => {
        return callApiMethod(() => stockMoves.importFromAccounting(from), {});
      },
      createStock: (data: Partial<IInvoiceStock>) => {
        return callApiMethod(() => connector.createStock(data), {});
      },
    }),
  ),
  withComputed((store) => ({
    itemsOptions: computed(() => store.items()?.items ?? []),
  })),
);
